import React, { useEffect, useState } from "react";
import combinedReportService, {
  FilterCondition,
  CombinedBooking,
  CombinedFilters,
  Project,
  CombinedTotals,
} from "../services/combinedReportService";
import "../css/FollowUpReport.css";

const CombinedIncomeReport: React.FC = () => {
  // Filters
  const [selectedProject, setSelectedProject] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  // Dynamic filter state
  const [filterLogic, setFilterLogic] = useState<"AND" | "OR">("AND");
  const [filterConditions, setFilterConditions] = useState<FilterCondition[]>([]);

  // Data state
  const [projects, setProjects] = useState<Project[]>([]);
  const [bookings, setBookings] = useState<CombinedBooking[]>([]);
  const [totals, setTotals] = useState<CombinedTotals>({
    regularExpected: 0,
    regularReceived: 0,
    regularPending: 0,
    hiddenExpected: 0,
    hiddenCollected: 0,
    hiddenPending: 0,
    totalExpected: 0,
    totalReceived: 0,
    totalPending: 0,
    pendingPercent: 0,
    receivedPercent: 0,
    totalPaymentCount: 0,
  });

  // Period state (active when payment date filter applied)
  const [periodActive, setPeriodActive] = useState(false);
  const [periodLabel, setPeriodLabel] = useState("");

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFilterBuilder, setShowFilterBuilder] = useState(false);

  // ── Preset management ──────────────────────────────────────────────────
  const PRESET_KEY = "combinedReportPresets";
  type Preset = {
    name: string;
    project: string;
    dateFrom: string;
    dateTo: string;
    logic: "AND" | "OR";
    conditions: FilterCondition[];
  };
  const loadSavedPresets = (): Preset[] => {
    try { return JSON.parse(localStorage.getItem(PRESET_KEY) || "[]"); } catch { return []; }
  };
  const [presets, setPresets] = useState<Preset[]>(loadSavedPresets);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [presetName, setPresetName] = useState("");

  const handleSavePreset = () => {
    if (!presetName.trim()) return;
    const updated = [...presets, {
      name: presetName.trim(),
      project: selectedProject,
      dateFrom,
      dateTo,
      logic: filterLogic,
      conditions: filterConditions,
    }];
    setPresets(updated);
    localStorage.setItem(PRESET_KEY, JSON.stringify(updated));
    setShowPresetModal(false);
    setPresetName("");
  };

  const handleLoadPreset = (index: number) => {
    const p = presets[index];
    setSelectedProject(p.project);
    setDateFrom(p.dateFrom);
    setDateTo(p.dateTo);
    setFilterLogic(p.logic);
    setFilterConditions(p.conditions);
    // Pass values directly so the fetch uses preset values immediately
    loadData({ project: p.project, dateFrom: p.dateFrom, dateTo: p.dateTo, logic: p.logic, conditions: p.conditions });
  };

  const handleDeletePreset = (index: number) => {
    const updated = presets.filter((_, i) => i !== index);
    setPresets(updated);
    localStorage.setItem(PRESET_KEY, JSON.stringify(updated));
  };

  // ── Column visibility management ───────────────────────────────────────
  const COL_KEY = "combinedReportCols";
  type ColVis = Record<string, boolean>;
  const DEFAULT_COLS: ColVis = {
    bookingId: true, bookingDate: true, customerName: true, customerContact: true,
    projectName: true, unitRef: true, unitType: true, wing: true,
    saleDeedExpected: true, saleDeedReceived: true, saleDeedPending: true,
    receivableExpected: true, receivableCollected: true, receivablePending: true,
    totalPending: true, pendingPercent: true,
    totalPayments: false, lastPaymentDate: true, lastPaymentAmt: false,
    daysSinceBooking: false, daysSinceLastPayment: true,
  };
  const COL_LABELS: Record<string, string> = {
    bookingId: "Booking ID", bookingDate: "Date", customerName: "Customer",
    customerContact: "Contact", projectName: "Project", unitRef: "Unit",
    unitType: "Type", wing: "Wing",
    saleDeedExpected: "Sale Deed Exp", saleDeedReceived: "Sale Deed Rcv", saleDeedPending: "Sale Deed Pend",
    receivableExpected: "Receivable Exp", receivableCollected: "Receivable Col", receivablePending: "Receivable Pend",
    totalPending: "Total Pending",
    pendingPercent: "Pending %", totalPayments: "Payments", lastPaymentDate: "Last Payment",
    lastPaymentAmt: "Last Amt", daysSinceBooking: "Days Since Booking", daysSinceLastPayment: "Days Since Last Pymt",
  };
  const initCols = (): ColVis => {
    try { return { ...DEFAULT_COLS, ...JSON.parse(localStorage.getItem(COL_KEY) || "{}") }; } catch { return DEFAULT_COLS; }
  };
  const [colVis, setColVis] = useState<ColVis>(initCols);
  const [showColManager, setShowColManager] = useState(false);

  const toggleCol = (key: string) => {
    const updated = { ...colVis, [key]: !colVis[key] };
    setColVis(updated);
    localStorage.setItem(COL_KEY, JSON.stringify(updated));
  };
  const resetCols = () => {
    setColVis(DEFAULT_COLS);
    localStorage.setItem(COL_KEY, JSON.stringify(DEFAULT_COLS));
  };
  const visColCount = Object.values(colVis).filter(Boolean).length;

  // Available filter fields and operators
  const filterFields = [
    { value: "pendingPercent", label: "Pending %" },
    { value: "receivedPercent", label: "Received %" },
    { value: "totalPending", label: "Total Pending" },
    { value: "totalExpected", label: "Total Expected" },
    { value: "totalReceived", label: "Total Received" },
    { value: "regularReceived", label: "Sale Deed Received" },
    { value: "regularPending", label: "Sale Deed Pending" },
    { value: "hiddenCollected", label: "Receivable Collected" },
    { value: "hiddenPending", label: "Receivable Pending" },
    { value: "totalPaymentCount", label: "Total Payment Count" },
    { value: "regularPaymentCount", label: "Sale Deed Payment Count" },
    { value: "hiddenCollectionCount", label: "Receivable Collection Count" },
    { value: "daysSinceBooking", label: "Days Since Booking" },
    { value: "daysSinceLastPayment", label: "Days Since Last Payment" },
    { value: "saleDeedPaymentDate", label: "Sale Deed Payment Date" },
    { value: "receivablePaymentDate", label: "Receivable Payment Date" },
    { value: "anyPaymentDate", label: "Any Payment Date" },
    { value: "paymentTerms", label: "Payment Terms" },
    { value: "unitType", label: "Unit Type" },
    { value: "wing", label: "Wing" },
    { value: "status", label: "Status" },
  ];

  const numericOperators = [
    { value: ">", label: ">" },
    { value: ">=", label: ">=" },
    { value: "<", label: "<" },
    { value: "<=", label: "<=" },
    { value: "=", label: "=" },
    { value: "between", label: "Between" },
  ];

  const textOperators = [
    { value: "=", label: "=" },
    { value: "!=", label: "!=" },
  ];

  const dateOperators = [
    { value: "between", label: "Between Dates" },
    { value: ">", label: "After" },
    { value: ">=", label: "On or After" },
    { value: "<", label: "Before" },
    { value: "<=", label: "On or Before" },
    { value: "last30Days", label: "Last 30 Days" },
    { value: "last90Days", label: "Last 90 Days" },
    { value: "lastMonth", label: "Last Month" },
    { value: "last3Months", label: "Last 3 Months" },
    { value: "last6Months", label: "Last 6 Months" },
    { value: "lastYear", label: "Last Year" },
  ];

  const operatorsByField: Record<string, { value: string; label: string }[]> = {
    pendingPercent: numericOperators,
    receivedPercent: numericOperators,
    totalPending: numericOperators,
    totalExpected: numericOperators,
    totalReceived: numericOperators,
    regularReceived: numericOperators,
    regularPending: numericOperators,
    hiddenCollected: numericOperators,
    hiddenPending: numericOperators,
    totalPaymentCount: [
      { value: "=", label: "=" },
      { value: "!=", label: "!=" },
      { value: ">", label: ">" },
      { value: ">=", label: ">=" },
      { value: "<", label: "<" },
      { value: "<=", label: "<=" },
    ],
    regularPaymentCount: [
      { value: "=", label: "=" },
      { value: "!=", label: "!=" },
      { value: ">", label: ">" },
      { value: ">=", label: ">=" },
      { value: "<", label: "<" },
      { value: "<=", label: "<=" },
    ],
    hiddenCollectionCount: [
      { value: "=", label: "=" },
      { value: "!=", label: "!=" },
      { value: ">", label: ">" },
      { value: ">=", label: ">=" },
      { value: "<", label: "<" },
      { value: "<=", label: "<=" },
    ],
    daysSinceBooking: numericOperators,
    daysSinceLastPayment: numericOperators,
    saleDeedPaymentDate: dateOperators,
    receivablePaymentDate: dateOperators,
    anyPaymentDate: dateOperators,
    paymentTerms: textOperators,
    unitType: textOperators,
    wing: textOperators,
    status: textOperators,
  };

  const textFields = ["paymentTerms", "unitType", "wing", "status"];
  const dateFields = ["saleDeedPaymentDate", "receivablePaymentDate", "anyPaymentDate"];
  const presetDateOperators = ["last30Days", "last90Days", "lastMonth", "last3Months", "last6Months", "lastYear"];

  // Load data on mount
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async (overrides?: { project?: string; dateFrom?: string; dateTo?: string; logic?: "AND" | "OR"; conditions?: FilterCondition[] }) => {
    try {
      setLoading(true);
      setError(null);

      const proj = overrides?.project ?? selectedProject;
      const df = overrides?.dateFrom ?? dateFrom;
      const dt = overrides?.dateTo ?? dateTo;
      const logic = overrides?.logic ?? filterLogic;
      const conds = overrides?.conditions ?? filterConditions;

      const filters: CombinedFilters | undefined =
        conds.length > 0
          ? { logic, conditions: conds }
          : undefined;

      const data = await combinedReportService.getCombinedReportData(
        proj !== "all" ? proj : undefined,
        df || undefined,
        dt || undefined,
        filters
      );

      setProjects(data.projects);
      setBookings(data.bookings);
      setTotals(data.totals);
      setPeriodActive(!!data.periodActive);
      setPeriodLabel(data.periodLabel || "");
    } catch (err: any) {
      console.error("Error loading combined report:", err);
      setError(err.response?.data?.error || "Failed to load report data");
    } finally {
      setLoading(false);
    }
  };

  // Filter handlers
  const handleApplyFilters = () => loadData();

  const handleClearFilters = () => {
    setSelectedProject("all");
    setDateFrom("");
    setDateTo("");
    setFilterConditions([]);
    setFilterLogic("AND");
    setPeriodActive(false);
    setPeriodLabel("");
    setTimeout(() => loadData(), 50);
  };

  // Dynamic filter builder handlers
  const handleAddCondition = () => {
    setFilterConditions([
      ...filterConditions,
      { field: "pendingPercent", operator: ">", value: 0 },
    ]);
  };

  const handleRemoveCondition = (index: number) => {
    setFilterConditions(filterConditions.filter((_, i) => i !== index));
  };

  const handleConditionChange = (
    index: number,
    field: keyof FilterCondition,
    value: any
  ) => {
    const updated = [...filterConditions];
    updated[index] = { ...updated[index], [field]: value };

    if (field === "field") {
      const firstOperator = operatorsByField[value]?.[0]?.value || "=";
      updated[index].operator = firstOperator as any;
      if (dateFields.includes(value)) {
        updated[index].value = firstOperator === "between" ? ["", ""] : "";
      } else {
        updated[index].value = textFields.includes(value) ? "" : 0;
      }
    }

    if (field === "operator") {
      const currentField = updated[index].field;
      if (presetDateOperators.includes(value)) {
        // Preset date operators don't need a value
        updated[index].value = "";
      } else if (value === "between") {
        updated[index].value = dateFields.includes(currentField) ? ["", ""] : [0, 0];
      } else if (dateFields.includes(currentField)) {
        updated[index].value = "";
      } else {
        updated[index].value = textFields.includes(currentField) ? "" : 0;
      }
    }

    setFilterConditions(updated);
  };

  const handleBetweenValueChange = (
    index: number,
    position: 0 | 1,
    value: number | string
  ) => {
    const updated = [...filterConditions];
    const currentValue = updated[index].value as [any, any];
    const newValue: [any, any] = [...currentValue] as [any, any];
    newValue[position] = value;
    updated[index].value = newValue;
    setFilterConditions(updated);
  };

  // Export functionality
  const handleExport = () => {
    if (bookings.length === 0) {
      alert("No data to export");
      return;
    }

    const headers = [
      "Booking ID", "Booking Date", "Customer Name", "Contact", "Project",
      "Unit", "Type", "Wing", "Payment Terms",
      "Sale Deed Expected", "Sale Deed Received",
      ...(periodActive ? [`SD Rcv (${periodLabel})`] : []),
      "Sale Deed Pending", "Sale Deed Payments",
      "Receivable Expected", "Receivable Collected",
      ...(periodActive ? [`Recv Col (${periodLabel})`] : []),
      "Receivable Pending", "Receivable Collections",
      "Total Pending", "Pending %",
      "Total Payments", "Last Payment Date", "Last Payment Amt",
      "Days Since Booking", "Days Since Last Payment", "Status",
    ];

    const rows = bookings.map((b) => [
      b.bookingId, b.bookingDate, b.customerName, b.customerContact, b.projectName,
      b.unitRef, b.unitType, b.wing, b.paymentTerms,
      b.regularExpected, b.regularReceived,
      ...(periodActive ? [b.saleDeedReceivedInPeriod || 0] : []),
      b.regularPending, b.regularPaymentCount,
      b.hiddenExpected, b.hiddenCollected,
      ...(periodActive ? [b.receivableCollectedInPeriod || 0] : []),
      b.hiddenPending, b.hiddenCollectionCount,
      b.totalPending, b.pendingPercent,
      b.totalPaymentCount, b.lastPaymentDate || "N/A", b.lastPaymentAmount || "N/A",
      b.daysSinceBooking, b.daysSinceLastPayment ?? "N/A", b.status,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `combined-income-report-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => window.print();

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <><div className="follow-up-report-container">
      <div className="report-header">
        <h1>📊 Combined Income Follow-Up Report</h1>
        <p className="report-subtitle">Unified view of Sale Deed payments and Receivable income per booking</p>
      </div>

      {/* ── Preset Bar ─────────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#f7fafc", border: "1px solid #e2e8f0", borderRadius: 8, marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <strong style={{ fontSize: "0.85rem", color: "#4a5568" }}>Saved Views:</strong>
          {presets.length === 0 ? (
            <span style={{ color: "#a0aec0", fontSize: "0.82rem" }}>No saved views yet</span>
          ) : (
            <select onChange={(e) => { if (e.target.value !== "") handleLoadPreset(Number(e.target.value)); e.target.value = ""; }} defaultValue="" style={{ padding: "5px 8px", border: "1px solid #cbd5e0", borderRadius: 6, fontSize: "0.85rem", minWidth: 180 }}>
              <option value="">— Load a view —</option>
              {presets.map((p, i) => <option key={i} value={i}>{p.name}</option>)}
            </select>
          )}
          {presets.length > 0 && (
            <select onChange={(e) => { if (e.target.value !== "") { handleDeletePreset(Number(e.target.value)); e.target.value = ""; } }} defaultValue="" style={{ padding: "5px 8px", border: "1px solid #fed7d7", borderRadius: 6, fontSize: "0.85rem", color: "#c53030", minWidth: 140 }}>
              <option value="">— Delete view —</option>
              {presets.map((p, i) => <option key={i} value={i}>{p.name}</option>)}
            </select>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setShowPresetModal(true)} style={{ padding: "6px 14px", background: "#d69e2e", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: "0.85rem", fontWeight: 500 }}>
            💾 Save Current View
          </button>
          <button onClick={() => setShowColManager(!showColManager)} style={{ padding: "6px 14px", background: "#319795", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: "0.85rem", fontWeight: 500 }}>
            🗂 Columns ({visColCount})
          </button>
        </div>
      </div>

      {/* ── Column Manager ─────────────────────────────────────────────── */}
      {showColManager && (
        <div style={{ background: "#f7fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: 14, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <strong style={{ color: "#2d3748" }}>Choose Visible Columns</strong>
            <button onClick={resetCols} style={{ padding: "4px 12px", background: "#edf2f7", color: "#4a5568", border: "1px solid #e2e8f0", borderRadius: 5, cursor: "pointer", fontSize: "0.8rem" }}>Reset to Default</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 6 }}>
            {Object.keys(DEFAULT_COLS).map((key) => (
              <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 10px", background: "white", border: "1px solid #e2e8f0", borderRadius: 5, cursor: "pointer", fontSize: "0.85rem" }}>
                <input type="checkbox" checked={!!colVis[key]} onChange={() => toggleCol(key)} style={{ accentColor: "#3182ce" }} />
                {COL_LABELS[key]}
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div className="filter-bar" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyFilters(); } }}>
        <div className="filter-group">
          <label>Project:</label>
          <select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)}>
            <option value="all">All Projects</option>
            {projects.map((proj) => (
              <option key={proj.id} value={proj.id.toString()}>
                {proj.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Booking Date From:</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>

        <div className="filter-group">
          <label>Booking Date To:</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>

        <div className="filter-actions">
          <button onClick={handleApplyFilters} className="btn-apply">
            Apply Filters
          </button>
          <button onClick={handleClearFilters} className="btn-clear">
            Clear Filters
          </button>
          <button
            onClick={() => setShowFilterBuilder(!showFilterBuilder)}
            className="btn-advanced"
          >
            {showFilterBuilder ? "Hide" : "Show"} Advanced Filters
          </button>
        </div>
      </div>

      {/* Advanced Filter Builder */}
      {showFilterBuilder && (
        <div className="filter-builder">
          <div className="filter-builder-header">
            <h3>🔍 Advanced Filter Builder</h3>
            <div className="logic-toggle">
              <label>Logic:</label>
              <select
                value={filterLogic}
                onChange={(e) => setFilterLogic(e.target.value as "AND" | "OR")}
              >
                <option value="AND">AND (All conditions must match)</option>
                <option value="OR">OR (Any condition can match)</option>
              </select>
            </div>
          </div>

          <div className="conditions-list">
            {filterConditions.map((condition, index) => (
              <div key={index} className="condition-row">
                <span className="condition-number">{index + 1}.</span>

                <select
                  value={condition.field}
                  onChange={(e) => handleConditionChange(index, "field", e.target.value)}
                >
                  {filterFields.map((field) => (
                    <option key={field.value} value={field.value}>
                      {field.label}
                    </option>
                  ))}
                </select>

                <select
                  value={condition.operator}
                  onChange={(e) => handleConditionChange(index, "operator", e.target.value)}
                >
                  {operatorsByField[condition.field]?.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>

                {/* Preset date operators (no input needed) */}
                {presetDateOperators.includes(condition.operator) ? (
                  <span style={{ color: '#718096', fontStyle: 'italic', fontSize: '0.85rem', padding: '6px 0' }}>Auto-calculated</span>
                ) : condition.operator === "between" && dateFields.includes(condition.field) ? (
                  <div className="between-inputs">
                    <input
                      type="date"
                      value={(condition.value as [string, string])[0] || ""}
                      onChange={(e) => handleBetweenValueChange(index, 0, e.target.value)}
                    />
                    <span>to</span>
                    <input
                      type="date"
                      value={(condition.value as [string, string])[1] || ""}
                      onChange={(e) => handleBetweenValueChange(index, 1, e.target.value)}
                    />
                  </div>
                ) : dateFields.includes(condition.field) ? (
                  <input
                    type="date"
                    value={condition.value as string}
                    onChange={(e) => handleConditionChange(index, "value", e.target.value)}
                  />
                ) : condition.operator === "between" ? (
                  <div className="between-inputs">
                    <input
                      type="number"
                      value={(condition.value as [number, number])[0]}
                      onChange={(e) =>
                        handleBetweenValueChange(index, 0, parseFloat(e.target.value))
                      }
                      placeholder="Min"
                    />
                    <span>to</span>
                    <input
                      type="number"
                      value={(condition.value as [number, number])[1]}
                      onChange={(e) =>
                        handleBetweenValueChange(index, 1, parseFloat(e.target.value))
                      }
                      placeholder="Max"
                    />
                  </div>
                ) : textFields.includes(condition.field) ? (
                  <input
                    type="text"
                    value={condition.value as string}
                    onChange={(e) => handleConditionChange(index, "value", e.target.value)}
                    placeholder="Enter value"
                  />
                ) : (
                  <input
                    type="number"
                    value={condition.value as number}
                    onChange={(e) =>
                      handleConditionChange(index, "value", parseFloat(e.target.value))
                    }
                    placeholder="Enter value"
                  />
                )}

                <button onClick={() => handleRemoveCondition(index)} className="btn-remove">
                  ✕
                </button>
              </div>
            ))}
          </div>

          <button onClick={handleAddCondition} className="btn-add-condition">
            + Add Condition
          </button>
        </div>
      )}

      {/* Export/Print Actions */}
      <div className="report-actions">
        <button onClick={handleExport} className="btn-export">
          📥 Export CSV
        </button>
        <button onClick={handlePrint} className="btn-print">
          🖨️ Print Report
        </button>
      </div>

      {/* Loading/Error */}
      {loading && <div className="loading">Loading report data...</div>}
      {error && <div className="error">{error}</div>}

      {!loading && !error && (
        <>
          {/* Bookings Table */}
          <div className="table-container">
            <div style={{ padding: "8px 12px", background: "#f7fafc", borderBottom: "1px solid #e2e8f0", fontSize: "0.82rem", color: "#718096" }}>
              Showing {bookings.length} booking(s) · {visColCount} column(s) visible{periodActive && <span style={{ color: '#d69e2e', fontWeight: 600 }}> · Period: {periodLabel}</span>}
            </div>
            <table className="follow-up-table">
              <thead>
                <tr>
                  {colVis.bookingId && <th>Booking ID</th>}
                  {colVis.bookingDate && <th>Date</th>}
                  {colVis.customerName && <th>Customer</th>}
                  {colVis.customerContact && <th>Contact</th>}
                  {colVis.projectName && <th>Project</th>}
                  {colVis.unitRef && <th>Unit</th>}
                  {colVis.unitType && <th>Type</th>}
                  {colVis.wing && <th>Wing</th>}
                  {colVis.saleDeedExpected && <th>Sale Deed Exp</th>}
                  {colVis.saleDeedReceived && <th>Sale Deed Rcv</th>}
                  {periodActive && <th style={{ background: '#fefcbf', color: '#744210' }}>SD Rcv ({periodLabel})</th>}
                  {colVis.saleDeedPending && <th>Sale Deed Pend</th>}
                  {colVis.receivableExpected && <th>Receivable Exp</th>}
                  {colVis.receivableCollected && <th>Receivable Col</th>}
                  {periodActive && <th style={{ background: '#fefcbf', color: '#744210' }}>Recv Col ({periodLabel})</th>}
                  {colVis.receivablePending && <th>Receivable Pend</th>}
                  {colVis.totalPending && <th>Total Pending</th>}
                  {colVis.pendingPercent && <th>Pending %</th>}
                  {colVis.totalPayments && <th>Payments</th>}
                  {colVis.lastPaymentDate && <th>Last Payment</th>}
                  {colVis.lastPaymentAmt && <th>Last Amt</th>}
                  {colVis.daysSinceBooking && <th>Days Since Booking</th>}
                  {colVis.daysSinceLastPayment && <th>Days Since Last Pymt</th>}
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 ? (
                  <tr>
                    <td colSpan={(visColCount + (periodActive ? 2 : 0)) || 8} className="no-data">
                      No bookings found with current filters
                    </td>
                  </tr>
                ) : (
                  bookings.map((booking) => (
                    <tr
                      key={booking.bookingId}
                      className={
                        booking.pendingPercent > 70 ||
                        (booking.daysSinceLastPayment !== null && booking.daysSinceLastPayment > 90)
                          ? "high-risk-row"
                          : ""
                      }
                    >
                      {colVis.bookingId && <td>{booking.bookingId}</td>}
                      {colVis.bookingDate && <td>{booking.bookingDate}</td>}
                      {colVis.customerName && <td>{booking.customerName}</td>}
                      {colVis.customerContact && <td>{booking.customerContact}</td>}
                      {colVis.projectName && <td>{booking.projectName}</td>}
                      {colVis.unitRef && <td>{booking.unitRef}</td>}
                      {colVis.unitType && <td>{booking.unitType}</td>}
                      {colVis.wing && <td>{booking.wing}</td>}
                      {colVis.saleDeedExpected && <td>{formatCurrency(booking.regularExpected)}</td>}
                      {colVis.saleDeedReceived && <td>{formatCurrency(booking.regularReceived)}</td>}
                      {periodActive && <td style={{ background: '#fffff0' }}>{formatCurrency(booking.saleDeedReceivedInPeriod || 0)}</td>}
                      {colVis.saleDeedPending && <td>{formatCurrency(booking.regularPending)}</td>}
                      {colVis.receivableExpected && <td>{formatCurrency(booking.hiddenExpected)}</td>}
                      {colVis.receivableCollected && <td>{formatCurrency(booking.hiddenCollected)}</td>}
                      {periodActive && <td style={{ background: '#fffff0' }}>{formatCurrency(booking.receivableCollectedInPeriod || 0)}</td>}
                      {colVis.receivablePending && <td>{formatCurrency(booking.hiddenPending)}</td>}
                      {colVis.totalPending && <td>{formatCurrency(booking.totalPending)}</td>}
                      {colVis.pendingPercent && <td className="pending-percent">{booking.pendingPercent.toFixed(2)}%</td>}
                      {colVis.totalPayments && <td>{booking.totalPaymentCount}</td>}
                      {colVis.lastPaymentDate && <td>{booking.lastPaymentDate || "N/A"}</td>}
                      {colVis.lastPaymentAmt && <td>{booking.lastPaymentAmount ? formatCurrency(booking.lastPaymentAmount) : "N/A"}</td>}
                      {colVis.daysSinceBooking && <td>{booking.daysSinceBooking}</td>}
                      {colVis.daysSinceLastPayment && <td>{booking.daysSinceLastPayment ?? "N/A"}</td>}
                    </tr>
                  ))
                )}
              </tbody>
              {bookings.length > 0 && (
                <tfoot>
                  <tr className="totals-row">
                    <td colSpan={["bookingId","bookingDate","customerName","customerContact","projectName","unitRef","unitType","wing"].filter(k => colVis[k]).length || 1}>
                      <strong>TOTALS ({bookings.length} bookings)</strong>
                    </td>
                    {colVis.saleDeedExpected && <td><strong>{formatCurrency(totals.regularExpected)}</strong></td>}
                    {colVis.saleDeedReceived && <td><strong>{formatCurrency(totals.regularReceived)}</strong></td>}
                    {periodActive && <td style={{ background: '#fefcbf' }}><strong>{formatCurrency(totals.saleDeedReceivedInPeriod || 0)}</strong></td>}
                    {colVis.saleDeedPending && <td><strong>{formatCurrency(totals.regularPending)}</strong></td>}
                    {colVis.receivableExpected && <td><strong>{formatCurrency(totals.hiddenExpected)}</strong></td>}
                    {colVis.receivableCollected && <td><strong>{formatCurrency(totals.hiddenCollected)}</strong></td>}
                    {periodActive && <td style={{ background: '#fefcbf' }}><strong>{formatCurrency(totals.receivableCollectedInPeriod || 0)}</strong></td>}
                    {colVis.receivablePending && <td><strong>{formatCurrency(totals.hiddenPending)}</strong></td>}
                    {colVis.totalPending && <td><strong>{formatCurrency(totals.totalPending)}</strong></td>}
                    {colVis.pendingPercent && <td className="pending-percent"><strong>{totals.pendingPercent.toFixed(2)}%</strong></td>}
                    {colVis.totalPayments && <td><strong>{totals.totalPaymentCount}</strong></td>}
                    {colVis.lastPaymentDate && <td></td>}
                    {colVis.lastPaymentAmt && <td></td>}
                    {colVis.daysSinceBooking && <td></td>}
                    {colVis.daysSinceLastPayment && <td></td>}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>

      {/* Save Preset Modal */}
      {showPresetModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "white", borderRadius: 12, width: 420, maxWidth: "90vw", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid #e2e8f0" }}>
              <strong style={{ fontSize: "1.05rem", color: "#2d3748" }}>💾 Save Current View</strong>
              <button onClick={() => setShowPresetModal(false)} style={{ background: "none", border: "none", fontSize: "1.2rem", cursor: "pointer", color: "#a0aec0" }}>✕</button>
            </div>
            <div style={{ padding: 20 }}>
              <p style={{ color: "#718096", fontSize: "0.88rem", marginBottom: 14 }}>Saves your current project selection, date range, and all active filter conditions.</p>
              <label style={{ display: "block", fontWeight: 600, fontSize: "0.85rem", color: "#4a5568", marginBottom: 6 }}>View Name</label>
              <input
                type="text"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSavePreset(); }}
                placeholder="e.g. High Risk This Quarter"
                style={{ width: "100%", padding: "9px 12px", border: "1px solid #cbd5e0", borderRadius: 6, fontSize: "0.9rem", boxSizing: "border-box" }}
                autoFocus
              />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "14px 20px", borderTop: "1px solid #e2e8f0" }}>
              <button onClick={() => setShowPresetModal(false)} style={{ padding: "7px 16px", background: "#edf2f7", color: "#4a5568", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 500 }}>Cancel</button>
              <button onClick={handleSavePreset} disabled={!presetName.trim()} style={{ padding: "7px 16px", background: "#d69e2e", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, opacity: presetName.trim() ? 1 : 0.5 }}>Save View</button>
            </div>
          </div>
        </div>
      )}
    </>  );
};

export default CombinedIncomeReport;